import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Spinner, Alert, Tooltip, OverlayTrigger, Modal 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function Fournisseurs() {
    const navigate = useNavigate();
    const [fournisseurs, setFournisseurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'raison_sociale', direction: 'asc' });
    const [showEvalModal, setShowEvalModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedFournisseur, setSelectedFournisseur] = useState(null);
    const [fournisseurToDelete, setFournisseurToDelete] = useState(null);
    const [evaluation, setEvaluation] = useState({
        note: 5,
        critere_delai: 5,
        critere_qualite: 5,
        critere_prix: 5,
        commentaire: ''
    });
    const [evalLoading, setEvalLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        fetchFournisseurs();
        fetchStats();
    }, []);

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const fetchFournisseurs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setFournisseurs(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const handleDelete = async () => {
        if (!fournisseurToDelete) return;
        
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?id=${fournisseurToDelete.id_fournisseur}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                setShowDeleteModal(false);
                setFournisseurToDelete(null);
                fetchFournisseurs();
                fetchStats();
                showSuccess("✅ Fournisseur supprimé avec succès");
            } else {
                showError(result.message);
            }
        } catch (err) {
            showError("Erreur lors de la suppression");
        } finally {
            setDeleting(false);
        }
    };

    const handleEvaluer = (fournisseur) => {
        setSelectedFournisseur(fournisseur);
        setEvaluation({
            note: 5,
            critere_delai: 5,
            critere_qualite: 5,
            critere_prix: 5,
            commentaire: ''
        });
        setShowEvalModal(true);
    };

    const openDeleteModal = (fournisseur) => {
        setFournisseurToDelete(fournisseur);
        setShowDeleteModal(true);
    };

    const submitEvaluation = async () => {
        setEvalLoading(true);
        try {
            const token = localStorage.getItem("token");
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            
            const response = await fetch(`${API_BASE_URL}/fournisseurs.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'evaluer',
                    id_fournisseur: selectedFournisseur.id_fournisseur,
                    ...evaluation,
                    id_utilisateur: user.id_utilisateur || 1
                })
            });
            
            const result = await response.json();
            
            if (result.success) {
                setShowEvalModal(false);
                showSuccess("⭐ Évaluation enregistrée avec succès");
                fetchFournisseurs();
                fetchStats();
            } else {
                showError(result.message);
            }
        } catch (err) {
            showError("Erreur lors de l'évaluation");
        } finally {
            setEvalLoading(false);
        }
    };

    const getNoteStars = (note) => {
        const noteValue = note ? Math.round(parseFloat(note)) : 0;
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <i key={i} className={`ri-star-${i <= noteValue ? 'fill' : 'line'} text-warning`} style={{ fontSize: '0.9rem' }}></i>
            );
        }
        return stars;
    };

    const getStatutBadge = (fournisseur) => {
        if (fournisseur.nb_commandes > 0) {
            return (
                <span className="status-badge" style={{ background: '#10b98115', color: '#10b981' }}>
                    <i className="ri-checkbox-circle-line me-1 fs-10"></i>
                    Actif
                </span>
            );
        }
        return (
            <span className="status-badge" style={{ background: '#64748b15', color: '#64748b' }}>
                <i className="ri-user-unfollow-line me-1 fs-10"></i>
                Inactif
            </span>
        );
    };

    const filteredFournisseurs = useMemo(() => {
        return fournisseurs
            .filter(f => 
                f.raison_sociale?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.code_fournisseur?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.email?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.telephone?.includes(filterText)
            )
            .sort((a, b) => {
                if (sortConfig.key === 'raison_sociale') {
                    return sortConfig.direction === 'asc' 
                        ? a.raison_sociale?.localeCompare(b.raison_sociale)
                        : b.raison_sociale?.localeCompare(a.raison_sociale);
                }
                if (sortConfig.key === 'total_achats') {
                    return sortConfig.direction === 'asc' 
                        ? (a.total_achats || 0) - (b.total_achats || 0)
                        : (b.total_achats || 0) - (a.total_achats || 0);
                }
                return 0;
            });
    }, [fournisseurs, filterText, sortConfig]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)} 
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}>
            <Button 
                variant={variant} 
                size="sm" 
                onClick={onClick} 
                className="action-btn"
            >
                <i className={icon} style={{ fontSize: '1rem' }}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des fournisseurs...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchFournisseurs}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-truck-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Fournisseurs</h4>
                                    <p className="text-muted small mb-0">
                                        {stats ? `${stats.total_fournisseurs || 0} fournisseurs enregistrés` : 'Gérez vos fournisseurs et leurs évaluations'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="primary" 
                                onClick={() => navigate('/achats/fournisseursAjouter')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={{ background: '#21ACCD', border: 'none' }}
                            >
                                <i className="ri-add-line"></i>
                                Nouveau fournisseur
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm={6} xl={4}>
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon">
                                        <i className="ri-truck-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Total fournisseurs</span>
                                        <h3 className="kpi-value">{stats.total_fournisseurs || 0}</h3>
                                        <span className="kpi-unit">+{stats.nouveaux_ajd || 0} aujourd'hui</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={4}>
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon">
                                        <i className="ri-checkbox-circle-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Fournisseurs actifs</span>
                                        <h3 className="kpi-value">{stats.fournisseurs_actifs || 0}</h3>
                                        <span className="kpi-unit">avec commandes récentes</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={4}>
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon">
                                        <i className="ri-star-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Note moyenne</span>
                                        <h3 className="kpi-value">{stats.note_moyenne ? parseFloat(stats.note_moyenne).toFixed(1) : 'N/A'}</h3>
                                        <span className="kpi-unit">/5 satisfaction</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={8}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par nom, code, email, téléphone..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={4} className="d-flex justify-content-end align-items-center">
                                    <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                        <i className="ri-stack-line me-1"></i>
                                        {filteredFournisseurs.length} fournisseur(s)
                                    </span>
                                    <Button
                                        variant="link"
                                        className="text-muted ms-3"
                                        onClick={() => setShowFilters(!showFilters)}
                                    >
                                        <i className={`ri-equalizer-line fs-5 ${showFilters ? 'text-primary' : ''}`}></i>
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau moderne */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Fournisseur</th>
                                            <th>Contact</th>
                                            <SortableHeader field="total_achats">Total achats</SortableHeader>
                                            <th>Dernière commande</th>
                                            <th>Note</th>
                                            <th>Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredFournisseurs.length > 0 ? (
                                            filteredFournisseurs.map((f) => (
                                                <tr key={f.id_fournisseur} className="fournisseur-row">
                                                    <td className="ps-4">
                                                        <Link to={`/achats/fournisseurs/${f.id_fournisseur}`} className="table-link">
                                                            {f.raison_sociale}
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column">
                                                            {f.telephone && (
                                                                <small className="text-muted">
                                                                    <i className="ri-phone-line me-1 fs-10"></i>
                                                                    {f.telephone}
                                                                </small>
                                                            )}
                                                            {f.email && (
                                                                <small className="text-muted">
                                                                    <i className="ri-mail-line me-1 fs-10"></i>
                                                                    {f.email}
                                                                </small>
                                                            )}
                                                            {!f.telephone && !f.email && <span className="text-muted">-</span>}
                                                        </div>
                                                    </td>
                                                    <td className="fw-bold text-primary">
                                                        {formatCurrency(f.total_achats || 0)}
                                                    </td>
                                                    <td>
                                                        {f.derniere_commande ? (
                                                            <small className="text-muted">
                                                                <i className="ri-calendar-line me-1 fs-10"></i>
                                                                {formatDate(f.derniere_commande)}
                                                            </small>
                                                        ) : <span className="text-muted">-</span>}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-1">
                                                            {getNoteStars(f.note_moyenne)}
                                                            {f.note_moyenne && (
                                                                <small className="text-muted">
                                                                    ({parseFloat(f.note_moyenne).toFixed(1)})
                                                                </small>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{getStatutBadge(f)}</td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <ActionButton
                                                                variant="outline-info"
                                                                icon="ri-eye-line"
                                                                onClick={() => navigate(`/achats/fournisseurs/${f.id_fournisseur}`)}
                                                                tooltip="Voir détails"
                                                            />
                                                            <ActionButton
                                                                variant="outline-primary"
                                                                icon="ri-pencil-line"
                                                                onClick={() => navigate(`/achats/fournisseurs/modifier/${f.id_fournisseur}`)}
                                                                tooltip="Modifier"
                                                            />
                                                            <ActionButton
                                                                variant="outline-warning"
                                                                icon="ri-star-line"
                                                                onClick={() => handleEvaluer(f)}
                                                                tooltip="Évaluer"
                                                            />
                                                            <ActionButton
                                                                variant="outline-danger"
                                                                icon="ri-delete-bin-line"
                                                                onClick={() => openDeleteModal(f)}
                                                                tooltip="Supprimer"
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-truck-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucun fournisseur trouvé</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Commencez par ajouter votre premier fournisseur"}
                                                        </p>
                                                        {filterText ? (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                                className="rounded-pill"
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="primary"
                                                                onClick={() => navigate('/achats/fournisseursAjouter')}
                                                                className="rounded-pill"
                                                                style={{ background: '#21ACCD', border: 'none' }}
                                                            >
                                                                <i className="ri-add-line me-2"></i>Ajouter un fournisseur
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                {/* Modal d'évaluation modernisé */}
                <Modal 
                    show={showEvalModal} 
                    onHide={() => setShowEvalModal(false)} 
                    centered 
                    className="modern-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#21ACCD' }}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className="ri-star-line text-white" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">Évaluer {selectedFournisseur?.raison_sociale}</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-4 pb-2">
                        <Form>
                            <Form.Group className="mb-4">
                                <Form.Label className="fw-semibold text-center d-block">Note globale</Form.Label>
                                <div className="d-flex justify-content-center gap-2">
                                    {[1,2,3,4,5].map(n => (
                                        <Button
                                            key={n}
                                            variant={evaluation.note >= n ? "warning" : "outline-warning"}
                                            onClick={() => setEvaluation({...evaluation, note: n})}
                                            className="rounded-circle p-2"
                                            style={{ width: '50px', height: '50px', fontSize: '1.2rem', borderWidth: '2px' }}
                                        >
                                            {n}
                                        </Button>
                                    ))}
                                </div>
                            </Form.Group>

                            <Row className="g-3 mb-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold">Délais</Form.Label>
                                        <Form.Select 
                                            value={evaluation.critere_delai}
                                            onChange={(e) => setEvaluation({...evaluation, critere_delai: parseInt(e.target.value)})}
                                            className="rounded-pill"
                                        >
                                            <option value="5">⭐ Excellent</option>
                                            <option value="4">⭐ Bon</option>
                                            <option value="3">⭐ Moyen</option>
                                            <option value="2">⭐ Médiocre</option>
                                            <option value="1">⭐ Mauvais</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold">Qualité</Form.Label>
                                        <Form.Select 
                                            value={evaluation.critere_qualite}
                                            onChange={(e) => setEvaluation({...evaluation, critere_qualite: parseInt(e.target.value)})}
                                            className="rounded-pill"
                                        >
                                            <option value="5">⭐ Excellent</option>
                                            <option value="4">⭐ Bon</option>
                                            <option value="3">⭐ Moyen</option>
                                            <option value="2">⭐ Médiocre</option>
                                            <option value="1">⭐ Mauvais</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold">Prix</Form.Label>
                                        <Form.Select 
                                            value={evaluation.critere_prix}
                                            onChange={(e) => setEvaluation({...evaluation, critere_prix: parseInt(e.target.value)})}
                                            className="rounded-pill"
                                        >
                                            <option value="5">💰 Très compétitif</option>
                                            <option value="4">💰 Compétitif</option>
                                            <option value="3">💰 Dans la moyenne</option>
                                            <option value="2">💰 Élevé</option>
                                            <option value="1">💰 Très élevé</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label className="small fw-semibold">Commentaire</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={evaluation.commentaire}
                                    onChange={(e) => setEvaluation({...evaluation, commentaire: e.target.value})}
                                    placeholder="Votre avis sur ce fournisseur..."
                                    className="rounded-3"
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowEvalModal(false)}
                            className="px-4 rounded-pill"
                            disabled={evalLoading}
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="warning" 
                            onClick={submitEvaluation} 
                            className="px-5 rounded-pill"
                            disabled={evalLoading}
                            style={{ background: '#f59e0b', border: 'none' }}
                        >
                            {evalLoading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Enregistrement...
                                </>
                            ) : (
                                <>
                                    <i className="ri-star-line me-2"></i>Évaluer
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal de confirmation de suppression modernisé */}
                <Modal 
                    show={showDeleteModal} 
                    onHide={() => setShowDeleteModal(false)} 
                    centered 
                    className="modern-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#dc2626' }}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className="ri-delete-bin-line text-white" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">Confirmer la suppression</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-4 text-center">
                        <p className="text-muted mb-3">
                            Êtes-vous sûr de vouloir supprimer le fournisseur ?
                        </p>
                        <div className="bg-light p-3 rounded-3 mb-3">
                            <strong className="d-block text-primary">{fournisseurToDelete?.raison_sociale}</strong>
                            <small className="text-muted">Code: {fournisseurToDelete?.code_fournisseur}</small>
                        </div>
                        <Alert variant="danger" className="rounded-3">
                            <i className="ri-alert-line me-2"></i>
                            Cette action est irréversible. Toutes les données associées seront supprimées.
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowDeleteModal(false)}
                            className="px-4 rounded-pill"
                            disabled={deleting}
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleDelete} 
                            className="px-5 rounded-pill"
                            disabled={deleting}
                            style={{ background: '#dc2626', border: 'none' }}
                        >
                            {deleting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Suppression...
                                </>
                            ) : (
                                <>
                                    <i className="ri-delete-bin-line me-2"></i>
                                    Confirmer
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-warning { border-left-color: #f59e0b; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                .btn-outline-primary:hover {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                }
                .btn-outline-warning:hover {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                    border-color: #f59e0b;
                    color: white;
                }
                .btn-outline-danger:hover {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    border-color: #ef4444;
                    color: white;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .fournisseur-row {
                    transition: all 0.2s ease;
                }
                
                .fournisseur-row:hover {
                    background: #f8fafc;
                }
                
                /* Modern Modal */
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                    overflow: hidden;
                }
                
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}